import * as Diff from "diff";
import { useATCPlan } from "../../store/ATCPlanState";
import { makeStyles } from "@material-ui/core/styles";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { highlightPointsState } from "../../store/HighlightPointsState";
import { useRecoilState } from "recoil";

const useStyles = makeStyles(() => ({
  box: {
    height: "calc(100% - 48px)"
  },
  wrapper: {
    height: "100%",
  },
  editor: {
    height: "100%",
    padding: "1px",
    border: "solid 1px #999",
    borderRadius: "5px",
    textAlign: "left",
    fontFamily: "Arial",
    fontSize: "12px",
    color: "rgba(0,0,0,.87)",
    overflowY: 'auto',
    "&:empty": {
      "&:before": {
        content: "attr(placeholder)",
        pointerEvents: 'none',
        opacity: "0.5",
        display:'block'
      }
    }
  }
}));

const PlanEditor = forwardRef(({onChange, revision}, ref) => {
  const {atcPlan} = useATCPlan();
  const [highlight, setHighlight] = useRecoilState(highlightPointsState);
  const editable = navigator.userAgent.toLowerCase().indexOf('firefox') === -1 ? 'plaintext-only' : true;
  const classes = useStyles();
  const [html, setHtml] = useState("");
  const inputRef = useRef(null)

  useEffect(() => {
    const original = formatPlan(atcPlan.original.text);
    const edit = formatPlan(atcPlan.edit.text);
    const planText = planDiff(original, edit);
    const formatPlanText = formatTag(planText);
    inputRef.current.innerHTML = formatPlanText;
    setHtml(formatPlanText);
  }, [atcPlan.original.text, atcPlan.edit.text, revision]);

  useEffect(() => {
    if (highlight[0]?.source === 'editor' || highlight[0]?.type === 'plane') {
      return;
    }

    //editor発信でhighlightを解除した場合なにもしない(キャレット位置を保持するため）
    if (highlight.length === 0 && inputRef.current.innerHTML.indexOf('<em>') === -1) {
      return;
    }

    let _html = inputRef.current.innerHTML.replace(/<em>|<\/em>/g, '');

    if (highlight.length !== 0) {
      const queryInfo = getSearchQuery(atcPlan, highlight);
      if (!queryInfo) {
        return;
      }

      if (queryInfo.isAirway) {

        //airwayのfixのoffset(出現位置)のmin/max
        const offset = {
          min: _html.length,
          max: 0
        };

        const setOffSet = (_offset) => {
          if (offset.min > _offset) offset.min = _offset;
          if (offset.max < _offset) offset.max = _offset;
        }

        const isBetweenOffset = (_offset) => {
          return offset.min < _offset && _offset < offset.max
        };

        for (let i = 0; i < queryInfo.query.length; i++) {
          const q = queryInfo.query[i];
          const reg = RegExp(q, 'g');
          //airwayの名称はqueryの最終要素に設定されている
          const isAirwayName = queryInfo.query.length - 1 === i;

          _html = _html.replace(reg, (hit, _, __, offset) => {
            if (!isAirwayName) {
              //airwayのfixのoffset(出現位置)のmin/maxを更新
              setOffSet(offset);
              return `<em>${hit}</em>`;
            } else {
              //offset内にあるAirway名称だけをハイライト
              if (isBetweenOffset(offset)) {
                return `<em>${hit}</em>`;
              } else {
                return hit;
              }
            }
          });
        }
      } else {
        queryInfo.query.forEach(q => {
          const reg = RegExp(q, 'g');
          _html = _html.replace(reg, (hit) => {
            return `<em>${hit}</em>`;
          });
        });
      }

    }
    inputRef.current.innerHTML = _html;

  }, [atcPlan, highlight]);

  useImperativeHandle(ref, () => ({
    clear: () => {
      inputRef.current.innerHTML = "";
    }
  }));

  const handleChange = e => {
    if (onChange) {
      onChange(e.target.innerText);
    }
  };

  const handleMouseEnter = e => {
    if (e.target.tagName.toLowerCase() !== 'pre') {
      const query = e.target.innerText;
      const highlight = [];
      if (e.target.classList.contains('dep')) {
        highlight.push({
          source: 'editor',
          type: 'fix',
          name: atcPlan.edit.json.dep.icao,
          lon: atcPlan.edit.json.dep.lon,
          lat: atcPlan.edit.json.dep.lat
        });
      } else if (e.target.classList.contains('arr')) {
        highlight.push({
          source: 'editor',
          type: 'fix',
          name: atcPlan.edit.json.arr.icao,
          lon: atcPlan.edit.json.arr.lon,
          lat: atcPlan.edit.json.arr.lat
        });
      } else {
        const target = e.target.parentElement.tagName.toLowerCase() === "ins" ? e.target.parentElement : e.target
        for (const p of atcPlan.edit.json.route) {
          if (p.airwayName) {
            if (p.airwayName === query) {
              //前後のfix名判定
              const prevFix = extractFixName(target.previousElementSibling.innerText);
              const afterFix = extractFixName(target.nextElementSibling.innerText);
              if (p.airway[0].name === prevFix || p.airway[p.airway.length - 1].name === afterFix) {
                p.airway.forEach(a => {
                  highlight.push({
                    source: 'editor',
                    type: 'airway',
                    name: a.name,
                    lon: a.lon,
                    lat: a.lat
                  })
                });
              }
            }
            //airwayの中身が選択されているケースを考慮
            let find = false;
            for (const ap of p.airway) {
              if (ap.name === query) {
                highlight.push({
                  source: 'editor',
                  type: 'fix',
                  name: ap.name,
                  lon: ap.lon,
                  lat: ap.lat
                });
                find = true;
                break;
              }
            }
            if (find) {
              break;
            }
          } else if (p.name === query) {
            highlight.push({
              source: 'editor',
              type: 'fix',
              name: p.name,
              lon: p.lon,
              lat: p.lat
            });
            break;
          }
        }
      }
      if (highlight.length) {
        setHighlight(highlight);
      }
    }
  }

  const handleMouseOut = () => {
    if (html !== "") {
      setHighlight([]);
    }
  };

  return (
    <div className={classes.box}>
      <div className={`atc-diff-box ${classes.wrapper}`}>
        <pre className={classes.editor}
             ref={inputRef}
             contentEditable={editable}
             spellCheck={false}
             translate={"no"}
             onInput={handleChange}
             onMouseOver={handleMouseEnter}
             onMouseOut={handleMouseOut}
             placeholder={"Please paste the ATC Plan."}
             dangerouslySetInnerHTML={{__html: html}}
        />
      </div>
    </div>
  )
});

const formatPlan = str => {
  if (str === '') return str;

  let atcStr = str.replace(/\r\n/g, "\n").split("\n").map((line) => {
    const trimmedLine = line.trim();
    if(trimmedLine.indexOf("-") === -1) {
      return ` ${trimmedLine}`;
    } else {
      return trimmedLine;
    }
  }).join("");
  atcStr = atcStr.split("-").map((line, index) => {
    if(index === 0) {
      return line;
    }
    else if([3, 5, 6, 7, 8].includes(index)) {
      return `\n-${line}`;
    } else {
      return `-${line}`;
    }
  }).join("");

  return atcStr;
}
const formatTag = (planText) => {
  return planText.split('\n').map((line, index) => {
    switch (index) {
      case 2:
        return parseDep(line);
      case 3:
        return parseRoute(line);
      case 4:
        return parseArr(line);
      default:
        return line;
    }
  }).join('\n');
}
const parseDep = dep => {
  const m = dep.match(/([a-zA-Z]{4})(.*)/);
  if (m) {
    return `${dep.substring(0, m.index)}<span class="dep">${m[1]}</span>${m[2]}`
  } else {
    return dep;
  }
};

const parseArr = arr => {
  const m = arr.match(/([a-zA-Z]{4})(.*)/);
  if (m) {
    return `${arr.substring(0, m.index)}<span class="arr">${m[1]}</span>${m[2]}`
  } else {
    return arr;
  }
};

const parseRoute = route => {
  const diffGroup = route.split(/<ins>|<\/ins>/);
  let diffFlg = route.indexOf('<ins>') === 0;
  const parsed = [];
  for (let g of diffGroup) {
    if (g === '') continue;
    const pts = g.split(/\s/);
    const partial = [];
    for (let p of pts) {
      if (p === ""){
        partial.push("");
        continue;
      }

      const point = p.replace(/^(C\/)?(\w+)(\/)?(\w+)?$/, '$1<span>$2</span>$3$4');

      const parts = diffFlg ? `<ins>${point}</ins>` : point;
      partial.push(parts);
    }

    parsed.push(partial.join(' '));
    diffFlg = !diffFlg;
  }
  return parsed.join('');
};

const planDiff = (before, after) => {
  const difference = []
  const diff = Diff.diffWords(before, after);
  for (let i=0; i < diff.length; i++) {

    if (diff[i].added && diff[i + 1] && diff[i + 1].removed) {
      const swap = diff[i];
      diff[i] = diff[i + 1];
      diff[i + 1] = swap;
    }

    if (diff[i].removed) {
      // difference.push(<del>{diff[i].value}</del>);
    } else if (diff[i].added) {
      difference.push(`<ins>${diff[i].value}</ins>`);
    } else {
      difference.push(diff[i].value);
    }
  }
  return difference.join('');
}

const getSearchQuery = (atcPlan, highlight) => {
  if (highlight.length === 0 || highlight[0].source === 'editor' || atcPlan.edit.json.route === undefined) {
    return null;
  }

  const names = highlight.map(p=>{return p.name});
  const namesStr = names.join(' ');
  let query = [];
  let isAirway = false;
  for (const r of atcPlan.edit.json.route) {
    if (r.airway) {
      const apName = r.airway.map(p=>{return p.name});
      //airway選択
      if (namesStr === apName.join(' ')) {
        query = query.concat(apName);
        query = query.concat(r.airwayName);
        isAirway = true;
        break;
      } else if (apName.slice(1, apName.length - 1).includes(namesStr)) {
        //airway内(先頭末尾を除く)のfixが選択されている場合、airwayとfixをハイライト
        query = query.concat(apName);
        query.push(r.airwayName);
        isAirway = true;
        break;
      }
    }
  }
  if (!isAirway) {
    query = names;
  }

  const queryList = query.map((q) => {
    if (q.match('<span>')) {
      return q;
    } else {
      return `<(\\w+)( class=.*)?>${q}<\\/\\1>`;
    }
  });

  return {
    isAirway,
    query: queryList
  }
};

const extractFixName = (str) => {
  const m = str.match(/^(C\/)?(\w+)\/(\w+)$/);
  if (m) {
    return m[2];
  } else {
    return str;
  }
};
export default PlanEditor;